import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestPage, uploadLogo } from '../services/api';
import FlowAnimation from './FlowAnimation';
import { useT } from '../i18n/LangContext';
import AppNav from '../components/AppNav';

const roles = [
  'Product Designer',
  'Frontend Engineer',
  'HR Business Partner',
  'Data Analyst',
  'Customer Success Manager',
  'Marketing Lead',
  'Talent Acquisition Specialist',
  'DevOps Engineer',
  'UX Researcher',
  'Sales Director',
];

const statKeys = [
  { value: '12k+', key: 'statPagesDeployed' },
  { value: '94%', key: 'statFillRate' },
  { value: '~28s', key: 'statAvgPublish' },
  { value: '3.2x', key: 'statMoreApplicants' },
];

const defaultBrief = {
  jobTitle: '',
  company: '',
  salaryRange: '',
  location: '',
  employmentType: 'full-time',
  workMode: 'hybrid',
  seniority: 'mid-level',
  requirements: '',
  qualifications: '',
  employerBranding: '',
  benefits: '',
  pageStyle: 'editorial-bold',
  brandLogoUrl: '',
  primaryColor: '#0f1a12',
  secondaryColor: '#173126',
  accentColor: '#c6ff3e',
  designLocked: false,
};

const stageKeys = [
  { id: 'essentials', titleKey: 'stage1Title', subKey: 'stage1Sub' },
  { id: 'content', titleKey: 'stage2Title', subKey: 'stage2Sub' },
  { id: 'brand', titleKey: 'stage3Title', subKey: 'stage3Sub' },
];

const phaseKeys = ['phase1', 'phase2', 'phase3', 'phase4', 'phase5'];

function buildDescription(brief) {
  return [
    `Create a high-converting HR hiring landing page for this role: ${brief.jobTitle}.`,
    `Company: ${brief.company || 'Not specified'}.`,
    `Salary range: ${brief.salaryRange || 'Not specified'}.`,
    `Location: ${brief.location || 'Not specified'}.`,
    `Employment type: ${brief.employmentType}.`,
    `Work mode: ${brief.workMode}.`,
    `Seniority: ${brief.seniority}.`,
    `Required skills and experience: ${brief.requirements || 'Include practical requirements and core competencies.'}.`,
    `Qualifications: ${brief.qualifications || 'Include relevant education, certifications, and must-have qualifications.'}.`,
    `Employer branding: ${brief.employerBranding || 'Highlight company culture, mission, and why candidates should join.'}.`,
    `Benefits and perks: ${brief.benefits || 'Include meaningful benefits section.'}.`,
    `Visual style preference: ${brief.pageStyle}.`,
    `Brand logo URL: ${brief.brandLogoUrl || 'Not specified'}.`,
    `Brand colors: primary ${brief.primaryColor}, secondary ${brief.secondaryColor}, accent ${brief.accentColor}.`,
    `Design lock preference: ${brief.designLocked ? 'locked to provided brand palette' : 'adaptive palette allowed'}.`,
    'The page must include an attractive hero, role summary, responsibilities, qualifications, compensation transparency, benefits, and an application form.',
    'Include dynamic visual sections with images or illustrations and tasteful motion effects.',
  ].join('\n');
}

function useIntersect(options) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, options);
    observer.observe(el);
    return () => observer.disconnect();
  }, [options]);
  return [ref, visible];
}

export default function HomePage() {
  const [brief, setBrief] = useState(defaultBrief);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const logoInputRef = useRef(null);
  const logoUploadCancelledRef = useRef(false);
  const [activeStage, setActiveStage] = useState(0);
  const [generationPhase, setGenerationPhase] = useState(0);
  const [error, setError] = useState('');
  const provider = 'openai';
  const [wordIndex, setWordIndex] = useState(0);
  const [fade, setFade] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const intersectOptions = useMemo(() => ({ threshold: 0.2 }), []);
  const [statsRef, statsVisible] = useIntersect(intersectOptions);
  const { t, lang } = useT();
  const navigate = useNavigate();
  const userEmail = localStorage.getItem('userEmail') || '';

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setFade(true);
      setTimeout(() => {
        setWordIndex((v) => (v + 1) % roles.length);
        setFade(false);
      }, 320);
    }, 2400);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!loading) {
      setGenerationPhase(0);
      return undefined;
    }

    const phaseTimer = setInterval(() => {
      setGenerationPhase((prev) => Math.min(prev + 1, phaseKeys.length - 1));
    }, 1350);

    return () => clearInterval(phaseTimer);
  }, [loading]);

  const morphClass = useMemo(() => `ms-word${fade ? ' fade' : ''}`, [fade]);

  function updateBrief(field, value) {
    setBrief((prev) => ({ ...prev, [field]: value }));
  }

  function validateStage(stageIndex) {
    if (stageIndex === 0 && (!brief.jobTitle.trim() || !brief.company.trim())) {
      return t('errorStage0Required') || 'Role title and company are required.';
    }
    if (stageIndex === 1) {
      if (!brief.requirements.trim()) return t('errorRequirementsRequired') || 'Key Requirements are required.';
      if (!brief.qualifications.trim()) return t('errorQualificationsRequired') || 'Qualifications are required.';
      if (!brief.employerBranding.trim()) return t('errorEmployerBrandingRequired') || 'Employer Branding is required.';
      if (!brief.benefits.trim()) return t('errorBenefitsRequired') || 'Benefits & Perks are required.';
    }
    if (stageIndex === 2 && !brief.brandLogoUrl && !logoPreview) {
      return t('logoRequired') || 'Brand logo is required.';
    }
    if (stageIndex === 2 && logoUploading) {
      return 'Logo is still uploading. Please wait a moment.';
    }
    return '';
  }

  function goNextStage() {
    const stageError = validateStage(activeStage);
    if (stageError) {
      setError(stageError);
      return;
    }

    setError('');
    setActiveStage((prev) => Math.min(prev + 1, stageKeys.length - 1));
  }

  function goPrevStage() {
    setError('');
    setActiveStage((prev) => Math.max(prev - 1, 0));
  }

  async function onGenerate(e) {
    e.preventDefault();
    setError('');

    // Guard: if the user pressed Enter in an earlier stage's input, just advance
    if (activeStage < stageKeys.length - 1) {
      goNextStage();
      return;
    }

    const e0 = validateStage(0), e1 = validateStage(1), e2 = validateStage(2);
    const stageError = e0 || e1 || e2;
    if (stageError) {
      setError(stageError);
      if (e0) setActiveStage(0);
      else if (e1) setActiveStage(1);
      else setActiveStage(2);
      return;
    }

    setLoading(true);
    try {
      const description = buildDescription(brief);
      await requestPage({
        description,
        brand: {
          brandLogoUrl: brief.brandLogoUrl || undefined,
          primaryColor: brief.primaryColor || undefined,
          secondaryColor: brief.secondaryColor || undefined,
          accentColor: brief.accentColor || undefined,
          designLocked: Boolean(brief.designLocked),
        },
      });
      setSubmitted(true);
      setBrief(defaultBrief);
      setLogoPreview('');
      setActiveStage(0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="grain" />

      <AppNav activePage="home" />

      <div className="hero">
        <div className="hero-orb hero-orb-a" />
        <div className="hero-orb hero-orb-b" />
        <div className="hero-grid-overlay" />

        <div className="hero-inner">
          <div className={`hero-display${heroVisible ? ' hero-in' : ''}`}>
            <div className="hero-eyebrow-row">
              <span className="hero-eyebrow-dot" />
              <span className="hero-micro">{t('heroMicro')}</span>
            </div>
            <h1 className="hero-h1">
              <span className="hero-line">{t('heroLine1')}</span>
              <span className="hero-line"><span className="accent">{t('heroLine2')}</span></span>
              <span className="hero-line"><span className="hero-h1-sub">{t('heroLine3')}</span></span>
            </h1>
            <p className="hero-sub hero-sub-anim">
              {t('heroSub')}
            </p>

            <div ref={statsRef} className={`stat-row${statsVisible ? ' stat-row-in' : ''}`}>
              {statKeys.map((s, i) => (
                <div key={s.key} className="stat-item" style={{ animationDelay: `${i * 80}ms` }}>
                  <span className="stat-value">{s.value}</span>
                  <span className="stat-label">{t(s.key)}</span>
                </div>
              ))}
            </div>
          </div>

          {submitted && (
            <div className={`hr-form${heroVisible ? ' hr-form-in' : ''}`} style={{ textAlign: 'center', padding: '40px 32px' }}>
              <div style={{ fontSize: '40px', marginBottom: '16px' }}>✓</div>
              <h2 style={{ color: '#c6ff3e', fontSize: '22px', fontWeight: 800, marginBottom: '12px' }}>{t('homeRequestSubmitted')}</h2>
              <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '15px', lineHeight: 1.6, marginBottom: '28px' }}>
                {t('homeRequestBody')}
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button className="hr-generate-btn" type="button" onClick={() => { setSubmitted(false); }}>
                  {t('homeSubmitAnother')}
                </button>
                <button className="hr-nav-btn next" type="button" onClick={() => navigate('/crm')}>
                  {t('homeViewInCrm')}
                </button>
              </div>
            </div>
          )}

          {!submitted && <form
            className={`hr-form${heroVisible ? ' hr-form-in' : ''}`}
            onSubmit={onGenerate}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                e.preventDefault();
              }
            }}
          >
            <div className="hr-form-header">
              <span className="hr-form-title">{t('formTitle')}</span>
              <span className="hr-form-sub">{t('formStepOf', { current: activeStage + 1, total: stageKeys.length })} · {t(stageKeys[activeStage].subKey)}</span>
            </div>

            <div className="hr-stage-track" role="tablist" aria-label="Role brief stages">
              {stageKeys.map((stage, index) => {
                const stateClass = index < activeStage ? 'done' : index === activeStage ? 'active' : 'todo';
                return (
                  <button
                    key={stage.id}
                    type="button"
                    className={`hr-stage-pill ${stateClass}`}
                    onClick={() => {
                      if (index <= activeStage) {
                        setError('');
                        setActiveStage(index);
                      }
                    }}
                    disabled={index > activeStage || loading}
                  >
                    <span>{`0${index + 1}`}</span> {t(stage.titleKey)}
                  </button>
                );
              })}
            </div>

            {activeStage === 0 && (
              <>
                <div className="hr-field-group">
                  <label className="field-label" htmlFor="jobTitle">
                    <span className="field-label-glyph">◈</span> {t('labelJobTitle')} <span className="req">*</span>
                  </label>
                  <input
                    id="jobTitle"
                    className="field-input"
                    placeholder={t('placeholderJobTitle')}
                    value={brief.jobTitle}
                    onChange={(e) => updateBrief('jobTitle', e.target.value)}
                    required
                  />
                </div>

                <div className="hr-input-row two-col">
                  <div className="hr-field-group">
                    <label className="field-label" htmlFor="company">{t('labelCompany')} <span className="req">*</span></label>
                    <input
                      id="company"
                      className="field-input"
                      placeholder={t('placeholderCompany')}
                      value={brief.company}
                      onChange={(e) => updateBrief('company', e.target.value)}
                      required
                    />
                  </div>
                  <div className="hr-field-group">
                    <label className="field-label" htmlFor="salaryRange">{t('labelSalary')}</label>
                    <input
                      id="salaryRange"
                      className="field-input"
                      placeholder={t('placeholderSalary')}
                      value={brief.salaryRange}
                      onChange={(e) => updateBrief('salaryRange', e.target.value)}
                    />
                  </div>
                </div>

                <div className="hr-input-row two-col">
                  <div className="hr-field-group">
                    <label className="field-label" htmlFor="location">{t('labelLocation')}</label>
                    <input
                      id="location"
                      className="field-input"
                      placeholder={t('placeholderLocation')}
                      value={brief.location}
                      onChange={(e) => updateBrief('location', e.target.value)}
                    />
                  </div>
                  <div className="hr-field-group">
                    <label className="field-label" htmlFor="seniority">{t('labelSeniority')}</label>
                    <select id="seniority" className="hr-select" value={brief.seniority} onChange={(e) => updateBrief('seniority', e.target.value)}>
                      <option value="junior">{t('seniorityJunior')}</option>
                      <option value="mid-level">{t('seniorityMid')}</option>
                      <option value="senior">{t('senioritySenior')}</option>
                      <option value="lead">{t('seniorityLead')}</option>
                      <option value="director">{t('seniorityDirector')}</option>
                    </select>
                  </div>
                </div>

                <div className="hr-input-row two-col">
                  <div className="hr-field-group">
                    <label className="field-label" htmlFor="employmentType">{t('labelEmploymentType')}</label>
                    <select id="employmentType" className="hr-select" value={brief.employmentType} onChange={(e) => updateBrief('employmentType', e.target.value)}>
                      <option value="full-time">{t('typeFull')}</option>
                      <option value="part-time">{t('typePart')}</option>
                      <option value="contract">{t('typeContract')}</option>
                      <option value="internship">{t('typeInternship')}</option>
                    </select>
                  </div>
                  <div className="hr-field-group">
                    <label className="field-label" htmlFor="workMode">{t('labelWorkMode')}</label>
                    <select id="workMode" className="hr-select" value={brief.workMode} onChange={(e) => updateBrief('workMode', e.target.value)}>
                      <option value="onsite">{t('modeOnsite')}</option>
                      <option value="hybrid">{t('modeHybrid')}</option>
                      <option value="remote">{t('modeRemote')}</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {activeStage === 1 && (
              <>
                <div className="hr-field-group">
                  <label className="field-label" htmlFor="pageStyle">{t('labelPageStyle')} <span style={{ color: '#e74c3c' }}>*</span></label>
                  <select id="pageStyle" className="hr-select" value={brief.pageStyle} onChange={(e) => updateBrief('pageStyle', e.target.value)}>
                    <option value="editorial-bold">Editorial Bold</option>
                    <option value="minimal-corporate">Clean Corporate</option>
                    <option value="high-energy">High Energy</option>
                    <option value="premium-luxury">Premium Dark</option>
                  </select>
                </div>

                <div className="hr-field-group">
                  <label className="field-label" htmlFor="requirements">{t('labelRequirements')} <span style={{ color: '#e74c3c' }}>*</span></label>
                  <textarea
                    id="requirements"
                    className="hr-textarea"
                    placeholder={t('placeholderRequirements')}
                    value={brief.requirements}
                    onChange={(e) => updateBrief('requirements', e.target.value)}
                  />
                </div>

                <div className="hr-field-group">
                  <label className="field-label" htmlFor="qualifications">{t('labelQualifications')} <span style={{ color: '#e74c3c' }}>*</span></label>
                  <textarea
                    id="qualifications"
                    className="hr-textarea"
                    placeholder={t('placeholderQualifications')}
                    value={brief.qualifications}
                    onChange={(e) => updateBrief('qualifications', e.target.value)}
                  />
                </div>

                <div className="hr-field-group">
                  <label className="field-label" htmlFor="employerBranding">{t('labelEmployerBranding')} <span style={{ color: '#e74c3c' }}>*</span></label>
                  <textarea
                    id="employerBranding"
                    className="hr-textarea"
                    placeholder={t('placeholderEmployerBranding')}
                    value={brief.employerBranding}
                    onChange={(e) => updateBrief('employerBranding', e.target.value)}
                  />
                </div>

                <div className="hr-field-group">
                  <label className="field-label" htmlFor="benefits">{t('labelBenefits')} <span style={{ color: '#e74c3c' }}>*</span></label>
                  <textarea
                    id="benefits"
                    className="hr-textarea"
                    placeholder={t('placeholderBenefits')}
                    value={brief.benefits}
                    onChange={(e) => updateBrief('benefits', e.target.value)}
                  />
                </div>
              </>
            )}

            {activeStage === 2 && (
              <>
                {/* Brand Logo — required */}
                <div className="wizard-section-label">{t('labelLogoUrl')} <span style={{ color: '#e74c3c', fontWeight: 700 }}>*</span></div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml,image/avif,.png,.jpg,.jpeg,.gif,.webp,.svg,.avif"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setLogoUploading(true);
                    logoUploadCancelledRef.current = false;
                    try {
                      setLogoPreview(URL.createObjectURL(file));
                      const result = await uploadLogo(file);
                      if (!logoUploadCancelledRef.current) {
                        updateBrief('brandLogoUrl', result.url);
                      }
                    } catch (err) {
                      if (!logoUploadCancelledRef.current) {
                        setLogoPreview('');
                        updateBrief('brandLogoUrl', '');
                        setError(err.message);
                      }
                    } finally {
                      setLogoUploading(false);
                      e.target.value = '';
                    }
                  }}
                />
                {logoPreview || brief.brandLogoUrl ? (
                  <div className="logo-preview-row">
                    <div className="logo-preview-thumb">
                      <img src={logoPreview || brief.brandLogoUrl} alt="logo" />
                    </div>
                    <div className="logo-preview-info">
                      <span className="logo-preview-name">{(brief.brandLogoUrl || logoPreview).split('/').pop()}</span>
                      <span className="logo-preview-hint">{t('logoUploadHint')}</span>
                    </div>
                    <div className="logo-preview-actions">
                      <button type="button" className="logo-action-btn logo-action-change" onClick={(e) => { e.stopPropagation(); logoInputRef.current?.click(); }}>
                        {t('logoChangeBtn')}
                      </button>
                      <button type="button" className="logo-action-btn logo-action-remove" onClick={(e) => { e.stopPropagation(); e.preventDefault(); logoUploadCancelledRef.current = true; setLogoUploading(false); setLogoPreview(''); updateBrief('brandLogoUrl', ''); }}>
                        {t('logoRemoveBtn')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="logo-upload-btn"
                    disabled={logoUploading}
                    onClick={() => logoInputRef.current?.click()}
                  >
                    <span className="logo-upload-icon">{logoUploading ? '⏳' : '☁'}</span>
                    <span className="logo-upload-text">
                      <strong>{logoUploading ? t('logoUploading') : t('logoUploadBtn')}</strong>
                      <span>{t('logoUploadHint')}</span>
                    </span>
                  </button>
                )}

                {/* Brand Colors */}
                <div className="wizard-section-label" style={{ marginTop: '14px' }}>{t('labelBrandColors') || 'Brand Colors'}</div>
                <div className="color-picker-row">
                  {[
                    { id: 'primaryColor',   label: t('labelPrimaryColor'),   key: 'primaryColor'   },
                    { id: 'secondaryColor', label: t('labelSecondaryColor'), key: 'secondaryColor' },
                    { id: 'accentColor',    label: t('labelAccentColor'),    key: 'accentColor'    },
                  ].map(({ id, label, key }) => (
                    <div key={id} className="color-picker-card" onClick={() => document.getElementById(id).click()}>
                      <input
                        id={id}
                        type="color"
                        value={brief[key]}
                        onChange={(e) => updateBrief(key, e.target.value)}
                        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                      />
                      <div className="color-picker-swatch" style={{ background: brief[key] }} />
                      <div className="color-picker-info">
                        <span className="color-picker-label">{label}</span>
                        <span className="color-picker-hex">{brief[key].toUpperCase()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {error && (
              <div className="hr-error">
                <span>⊗</span> {error}
              </div>
            )}

            <div className="hr-form-footer">
              <div className="hr-footer-left">
                {userEmail ? (
                  <span className="hr-user-tag">
                    <span className="hr-user-dot" />
                    {userEmail}
                  </span>
                ) : (
                  <span className="hr-footer-hint">{loading ? t(phaseKeys[generationPhase]) : t('footerHint')}</span>
                )}

              </div>

              <div className="hr-stage-actions">
                {activeStage > 0 && (
                  <button className="hr-nav-btn" type="button" onClick={goPrevStage} disabled={loading}>
                    {t('btnBack')}
                  </button>
                )}

                {activeStage < stageKeys.length - 1 ? (
                  <button className="hr-nav-btn next" type="button" onClick={goNextStage} disabled={loading}>
                    {t('btnNextStep')}
                  </button>
                ) : (
                  <button className={`hr-generate-btn${loading ? ' loading' : ''}`} type="button" onClick={onGenerate} disabled={loading || logoUploading}>
                    {loading ? (
                      <span className="btn-loading-row">
                        <span className="btn-spinner" />
                        {t('homeSubmittingRequest')}
                      </span>
                    ) : (
                      <>Submit Request <span className="btn-arrow">→</span></>
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>}
        </div>
      </div>

      <div className="morph-strip">
        <span className="ms-label">{t('morphLabel')}</span>
        <div className={morphClass}>{roles[wordIndex]}</div>
        <div className="ms-divider" />
        <div className="ms-ticker-wrap">
          <div className="ms-ticker">
            {['Tech & Engineering', 'People & HR', 'Sales & Revenue', 'Design & Creative', 'Operations', 'Leadership & Exec', 'Finance', 'Legal', 'Marketing', 'Product Management'].map((t) => (
              <span key={t} className="ms-ticker-item">
                <span className="ms-ticker-dot" />
                {t}
              </span>
            ))}
            {['Tech & Engineering', 'People & HR', 'Sales & Revenue', 'Design & Creative', 'Operations', 'Leadership & Exec', 'Finance', 'Legal', 'Marketing', 'Product Management'].map((t) => (
              <span key={`dup-${t}`} className="ms-ticker-item" aria-hidden="true">
                <span className="ms-ticker-dot" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>

      <FlowAnimation />

    </>
  );
}
